import styles from 'css/src/components/app/BackgroundAsset.module.scss';
import mediaBadgeStyles from 'css/src/components/common/media-badge.module.scss';

import React from 'react';
import { useSelector } from 'react-redux';
import Immutable from 'immutable';
import classNames from 'classnames';
import { createSelector } from '@reduxjs/toolkit';

import type { AssetContainerModel } from 'js/src/client';
import {
    AssetContainerTypes,
    AssetTypes,
    ProviderEnum,
    SlideBackgroundModelTypeEnum,
    SourceEnum,
} from 'js/src/client';
import { BackgroundAssetAIMediaDropdown } from 'js/src/components/app/BackgroundAssetAIMediaDropdown';
import type { DragAssetTypes } from 'js/src/components/app/MediaDragAndDropManager';
import { DRAG_EVENTS, MiscDragAssetTypes } from 'js/src/components/app/MediaDragAndDropManager';
import {
    IconAsset,
    ImageAsset,
    VideoAsset,
    WaveformAsset,
} from 'js/src/components/common/BackgroundAsset';
import { Toast } from 'js/src/components/common/Toast';
import Button, {
    BUTTON_COLORS,
    BUTTON_SIZES,
    BUTTON_VARIANTS,
    IconButton,
    SecondaryButton,
} from 'js/src/components/common/ui/Button';
import type { DropdownItemProp, DropdownRef } from 'js/src/components/common/ui/Dropdown';
import { Dropdown } from 'js/src/components/common/ui/Dropdown';
import { EDITOR_TAB_OPTIONS, getCurrentEditorTab } from 'js/src/data/app';
import { logApplyFromBackgroundTab, logClickFromBackgroundTab } from 'js/src/data/click-logs';
import type { MediaFolderWithContents, SearchContextValueType } from 'js/src/data/media';
import {
    changeFolderForAsset,
    EDITOR_DAM_TAB_CONTEXTS,
    EDITOR_ELEMENTS_TAB_CONTEXTS,
    getAssetUuidFromAssetContainer,
    getGenerationFromMediaUuid,
    getIsFavouriteMedia,
    getIsSavingAIMediaForGeneration,
    getMediaQueryResultByIndex,
    getSearchResultById,
    immutableSlideBackgroundModelIsImmutableImageBackgroundModel,
    immutableSlideBackgroundModelIsImmutableVideoBackgroundModel,
    isAnimatedSticker,
    isAssetTypeMatchingSelectedAssetContainerType,
    isUploadSearchContextType,
    moveAssetToFolder,
    SEARCH_CONTEXT,
    selectActiveMediaFolder,
    setIsSimilarSearchContext,
    setSimilarSearchOriginalAsset,
    toggleFavoriteMedia,
    triggerSimilarAssetSearch,
    updateFolderThumbnail,
} from 'js/src/data/media';
import type { MediaDetailsModalContext } from 'js/src/data/modals';
import { MODAL_IDS, showModal } from 'js/src/data/modals';
import { getFocusedAssetId, getFocusedAssetType, getFocusedSceneId } from 'js/src/data/scenes';
import { handOffRecordingAppliedToTalkingHeadSceneOperation } from 'js/src/data/uploads/recorded-media-chat-handoff';
import type { StoreORMInterface } from 'js/src/data/store-orm';
import StoreORM from 'js/src/data/store-orm';
import {
    immutableAssetContainerModelIsBackground,
    immutableAssetContainerModelIsIcon,
    UploadsOperations,
} from 'js/src/data/uploads';
import type { ImmutableMap } from 'js/src/libs/consts';
import { FREE_PLAN_NAME, ImmutableFromJS, ImmutableToJS } from 'js/src/libs/consts';
import { isEditingContainerVisible } from 'js/src/libs/custom-utils';
import PremiumMediaChecker, { PREMIUM_LEVEL } from 'js/src/libs/premium-media-checker';
import { type RootState, useAppDispatch } from 'js/src/libs/RootState';
import SlideEventEmitter, { SLIDE_EVENTS } from 'js/src/libs/slide-event-emitter';
import {
    applyFreepikStaticStickerOperation,
    buildFreepikIconSourceData,
    setIconAssetForSceneOperation,
    setIconSouceDataForSceneOperation,
    setMediaAssetForSceneOperation,
    SetMediaAssetForSceneOperationMethod,
} from 'js/src/luminary';
import { useLuminaryOperations } from 'js/src/luminary/luminary-hooks';

enum OptionsDropdownActions {
    DELETE = 'delete',
    MOVE_TO_FOLDER = 'move-to-folder',
    MEDIA_INFO = 'media-info',
    FAVORITES = 'favorites',
}

const OptionsDropdownToggleProps = Immutable.Map({
    className: `media-options-dropdown-toggle btn-icon btn-media ${mediaBadgeStyles['media-badge']}`,
    icon: 'more_horiz',
});

/**
 * Some DAM API does not give video duration.
 * So we should only show duration if search context is not one of these DAMs
 */
const SEARCH_CONTEXT_MISSING_VIDEO_DURATION = [ProviderEnum.BYNDER, ProviderEnum.BRANDFOLDER];

const getDestinationFolderList = createSelector(
    (state: RootState) => state.media.all_uploads_folders,
    (state: RootState, searchContext: SearchContextValueType) =>
        selectActiveMediaFolder(state, searchContext),
    (state: RootState, searchContext: SearchContextValueType) => searchContext,
    (
        all_uploads_folders: StoreORMInterface<MediaFolderWithContents>,
        activeMediaFolder: number | null,
        searchContext: SearchContextValueType,
    ) => {
        const foldersOrm = new StoreORM<MediaFolderWithContents>(all_uploads_folders);
        const filteredFolders = foldersOrm.filter((folder) => {
            return (
                folder.get('id') !== activeMediaFolder &&
                folder.get('creation_source') === searchContext
            );
        });
        return ImmutableToJS(filteredFolders);
    },
);

type OwnProps = {
    media?: ImmutableMap<AssetContainerModel> | null; // confusingly an ownProp but ALSO what we get from the redux store
    media_id?: string | number;
    result_index: number; // should be -1 for assets the user doesn't have access to
    searchContext: SearchContextValueType | null;
    isFavoriteMedia?: boolean;
    isMediaQuerySuggestionResult?: boolean;
    isArticleMedia?: boolean;
    showAIMediaDropdown?: boolean;
    fullWidth?: boolean;
    hideControls?: boolean;
    category?: string;
};

const BackgroundAsset = React.memo(function BackgroundAsset({
    media: mediaProp,
    media_id,
    result_index,
    searchContext,
    isFavoriteMedia: isFavoriteMediaProp,
    isMediaQuerySuggestionResult,
    isArticleMedia,
    showAIMediaDropdown = true,
    fullWidth,
    hideControls,
    category,
}: OwnProps) {
    const dispatch = useAppDispatch();

    const {
        setIconAssetForScene,
        setIconSouceDataForScene,
        setMediaAssetForScene,
        applyFreepikStaticSticker,
        handOffRecordingAppliedToTalkingHeadScene,
    } = useLuminaryOperations({
        setMediaAssetForSceneOperation,
        setIconAssetForSceneOperation,
        setIconSouceDataForSceneOperation,
        applyFreepikStaticStickerOperation,
        handOffRecordingAppliedToTalkingHeadSceneOperation,
    });

    const mediaQueryResult = useSelector((state: RootState) =>
        !mediaProp && isMediaQuerySuggestionResult
            ? getMediaQueryResultByIndex(state, result_index, media_id as number)
            : null,
    );
    const searchResult = useSelector((state: RootState) => {
        if (mediaProp || isMediaQuerySuggestionResult || isArticleMedia) {
            return null;
        }
        if (!searchContext) throw new Error('Search context is not defined');
        // possibly null when switching between folders due to race condition
        return getSearchResultById(state, searchContext, media_id) ?? null;
    });
    // selector takes into account the active tab + active folder to return the correct media in active category
    const media = mediaProp ?? mediaQueryResult ?? searchResult;
    const activeFolderId = useSelector((state: RootState) =>
        searchResult && searchContext ? selectActiveMediaFolder(state, searchContext) : null,
    );
    const destinationFolderList = useSelector((state: RootState) =>
        searchResult && searchContext ? getDestinationFolderList(state, searchContext) : undefined,
    );
    const isFavoriteMedia = useSelector((state: RootState) => {
        if (media && !isFavoriteMediaProp) {
            const uuid = getAssetUuidFromAssetContainer(media);
            if (uuid) {
                return getIsFavouriteMedia(state, uuid);
            }
        }
        return isFavoriteMediaProp ?? false;
    });
    const isSavingAIMedia = useSelector((state: RootState) => {
        if (media && !isFavoriteMediaProp) {
            const uuid = getAssetUuidFromAssetContainer(media);
            if (uuid) {
                const generation = getGenerationFromMediaUuid(
                    state,
                    uuid,
                    SlideBackgroundModelTypeEnum.IMAGE,
                );
                if (generation) {
                    return getIsSavingAIMediaForGeneration(state, generation.uuid);
                }
            }
        }
        return false;
    });
    const focusedSceneId = useSelector(getFocusedSceneId);
    const selectedSceneAssetId = useSelector(getFocusedAssetId);
    const selectedSceneAssetType = useSelector(getFocusedAssetType);
    const selectedSceneAssetSceneId = useSelector(getFocusedSceneId);
    const isGenerativeMediaTabContext = useSelector(
        (state: RootState) => getCurrentEditorTab(state) === EDITOR_TAB_OPTIONS.GENERATIVE_MEDIA,
    );
    const isSticker =
        searchContext !== null &&
        (EDITOR_ELEMENTS_TAB_CONTEXTS as readonly string[]).includes(searchContext);

    const optionsDropdownRef = React.useRef<DropdownRef>(null);
    const backgroundAssetElementRef = React.useRef<HTMLDivElement | null>(null);
    const [showDelConfirm, setShowDelConfirm] = React.useState(false);
    const [uploadMenuIsOpen, setUploadMenuIsOpen] = React.useState(false);

    const closeUploadDropdown = React.useCallback(() => {
        if (optionsDropdownRef.current) {
            optionsDropdownRef.current.close();
        }
    }, []);

    const backgroundAssetCallbackRef = React.useCallback(
        (element: HTMLDivElement | null) => {
            if (backgroundAssetElementRef.current) {
                backgroundAssetElementRef.current.removeEventListener(
                    DRAG_EVENTS.DRAGGING,
                    closeUploadDropdown,
                );
            }
            backgroundAssetElementRef.current = element;
            if (element) {
                element.addEventListener(DRAG_EVENTS.DRAGGING, closeUploadDropdown);
            }
        },
        [closeUploadDropdown],
    );

    const menuAfterClose = React.useCallback(() => {
        setUploadMenuIsOpen(false);
    }, []);

    const menuAfterOpen = React.useCallback(() => {
        setUploadMenuIsOpen(true);
    }, []);

    const handleDeleteClick = React.useCallback(() => {
        setShowDelConfirm(true);
    }, []);

    const handleMoveFolder = React.useCallback(
        (folderId: number) => {
            if (media) {
                changeFolderForAsset(media, folderId)
                    .then((response) => {
                        dispatch(
                            moveAssetToFolder({
                                targetFolderId: folderId,
                                asset: media,
                                search_context: searchContext,
                                sourceFolderId: activeFolderId,
                            }),
                        );

                        const { new_src_folder_thumbnail_url, new_target_folder_thumbnail_url } =
                            response.data;

                        dispatch(
                            updateFolderThumbnail({
                                srcFolderId: activeFolderId,
                                srcThumbnailUrl: new_src_folder_thumbnail_url,
                                targetFolderId: folderId,
                                targetThumbnailUrl: new_target_folder_thumbnail_url,
                            }),
                        );
                    })
                    .catch(() => {
                        Toast.error(
                            'Something went wrong while moving your media asset. Please try again.',
                        );
                    });
            }
        },
        [dispatch, media, searchContext, activeFolderId],
    );

    const showDetails = React.useCallback(() => {
        if (!media) throw new Error('Media is not defined');

        const background = media.get(AssetContainerTypes.BACKGROUND);
        const backgroundType = background?.get('type') as SlideBackgroundModelTypeEnum;
        const isVideo = backgroundType === SlideBackgroundModelTypeEnum.VIDEO;
        const source = isVideo
            ? background?.get(SlideBackgroundModelTypeEnum.VIDEO)?.get('source')
            : background?.get(SlideBackgroundModelTypeEnum.IMAGE)?.get('source');
        const isAIGeneratedMedia =
            source === SourceEnum.SOURCE_AI_GENERATED || source === SourceEnum.SOURCE_AI_CHART;
        if (isAIGeneratedMedia) {
            if (!immutableAssetContainerModelIsBackground(media))
                throw new Error('can only show generative media details for background');
            dispatch(
                showModal({
                    modal_id: MODAL_IDS.GENERATIVE_MEDIA_DETAILS,
                    modal_context: ImmutableFromJS({
                        media: media.get(AssetContainerTypes.BACKGROUND).toJS(),
                        // When in the generative media tab, the media is unsaved
                        isMediaSaved: !isGenerativeMediaTabContext,
                    }),
                }),
            );
        } else {
            if (
                !immutableAssetContainerModelIsBackground(media) &&
                !immutableAssetContainerModelIsIcon(media)
            )
                throw new Error('can only show details for background');
            dispatch(
                showModal({
                    modal_id: MODAL_IDS.MEDIA_DETAILS,
                    modal_context: ImmutableFromJS<MediaDetailsModalContext>({
                        media: media.toJS(),
                    }),
                }),
            );
            if (!isMediaQuerySuggestionResult) {
                dispatch(logClickFromBackgroundTab(media, result_index));
            }
        }
    }, [media, dispatch, isMediaQuerySuggestionResult, isGenerativeMediaTabContext, result_index]);

    const onOptionsDropdownSelect = React.useCallback(
        (
            onSelectData: ImmutableMap<{
                type: OptionsDropdownActions;
                folderId?: number;
            }>,
        ) => {
            if (!media) throw new Error('Media is not defined');
            switch (onSelectData.get('type')) {
                case OptionsDropdownActions.DELETE: {
                    handleDeleteClick();
                    break;
                }
                case OptionsDropdownActions.MOVE_TO_FOLDER: {
                    const folderId = onSelectData.get('folderId');
                    if (!folderId) throw new Error('Folder ID is not defined');
                    handleMoveFolder(folderId);
                    break;
                }
                case OptionsDropdownActions.MEDIA_INFO: {
                    showDetails();
                    break;
                }
                case OptionsDropdownActions.FAVORITES: {
                    dispatch(toggleFavoriteMedia(media));
                    break;
                }
                default: {
                    throw Error('Expected to know the action type from the uploadDropdown!');
                }
            }
        },
        [media, dispatch, handleDeleteClick, handleMoveFolder, showDetails],
    );

    const blockContextMenu = React.useCallback((e: React.MouseEvent) => {
        // Getty and Shutterstock require that we block the right click menu, so do that here
        if (SERVER.DEBUG) {
            return;
        }
        e.preventDefault();
        e.stopPropagation();
    }, []);

    const handleDeleteAsset = React.useCallback(
        (e: React.MouseEvent) => {
            e.preventDefault();
            setShowDelConfirm(false);
            if (media) {
                dispatch(UploadsOperations.archiveUpload(media, searchContext));
            }
        },
        [dispatch, media, searchContext],
    );

    const hideDeleteConfirmation = React.useCallback((e: React.MouseEvent) => {
        e.preventDefault();
        setShowDelConfirm(false);
    }, []);

    const applyAssetToSelectedAssetContainer = React.useCallback(async () => {
        if (!media) throw new Error('Media is not defined');
        // stop playback (if playing)
        SlideEventEmitter.emit(SLIDE_EVENTS.GLOBAL_STOP_PREVIEW_PLAYBACK);

        if (!selectedSceneAssetSceneId) {
            return;
        }

        // set
        const isAssetMatchingContainer = isAssetTypeMatchingSelectedAssetContainerType(
            media,
            selectedSceneAssetType,
            isSticker,
        );
        const targetSceneAssetId = isAssetMatchingContainer ? selectedSceneAssetId : null;

        if (immutableAssetContainerModelIsIcon(media)) {
            setIconAssetForScene(selectedSceneAssetSceneId, media, {
                targetAssetId: targetSceneAssetId,
            });
        } else if (immutableAssetContainerModelIsBackground(media)) {
            const background = media.get(AssetContainerTypes.BACKGROUND);
            const bgImage = background?.get(SlideBackgroundModelTypeEnum.IMAGE);

            if (bgImage && bgImage.get('source') === SourceEnum.SOURCE_FREEPIK) {
                if (isAnimatedSticker(bgImage.get('thumbnail_url'))) {
                    setIconSouceDataForScene(
                        selectedSceneAssetSceneId,
                        buildFreepikIconSourceData(
                            bgImage.get('thumbnail_url'),
                            bgImage.get('source_id'),
                        ),
                        { targetAssetId: targetSceneAssetId },
                    );
                } else {
                    await applyFreepikStaticSticker(
                        selectedSceneAssetSceneId,
                        background,
                        bgImage.get('source_id'),
                        {
                            targetAssetId: targetSceneAssetId,
                            method: SetMediaAssetForSceneOperationMethod.CLICK,
                        },
                    );
                }
            } else {
                // Clicking a recording applies it to the selected scene; when that
                // scene is a talking head this runs the legacy recommendation
                // pipeline, so chat users' recordings are handed to the chat
                // instead, which rebuilds the supporting scenes itself.
                const handoff = await handOffRecordingAppliedToTalkingHeadScene({
                    sceneId: selectedSceneAssetSceneId,
                    mediaSourceData: background,
                    isSticker,
                });
                if (handoff !== 'notApplicable') {
                    if (handoff === 'failed') {
                        Toast.error('Sorry, something went wrong with your recording.');
                    }
                    return;
                }
                setMediaAssetForScene({
                    sceneId: selectedSceneAssetSceneId,
                    mediaSourceData: background,
                    targetAssetId: targetSceneAssetId,
                    method: SetMediaAssetForSceneOperationMethod.CLICK,
                    isRecommendedMedia: isMediaQuerySuggestionResult,
                    isSticker,
                });
            }
        }

        if (!isArticleMedia && !isGenerativeMediaTabContext) {
            dispatch(logApplyFromBackgroundTab(media, result_index));
        }
    }, [
        media,
        selectedSceneAssetSceneId,
        selectedSceneAssetType,
        selectedSceneAssetId,
        setIconAssetForScene,
        setIconSouceDataForScene,
        setMediaAssetForScene,
        applyFreepikStaticSticker,
        handOffRecordingAppliedToTalkingHeadScene,
        isMediaQuerySuggestionResult,
        isArticleMedia,
        isGenerativeMediaTabContext,
        dispatch,
        result_index,
        isSticker,
    ]);

    const applyAssetToFocusedScene = React.useCallback(async () => {
        if (!media) throw new Error('Media is not defined');
        // stop playback (if playing)
        SlideEventEmitter.emit(SLIDE_EVENTS.GLOBAL_STOP_PREVIEW_PLAYBACK);

        if (!focusedSceneId) {
            return;
        }

        // set
        if (immutableAssetContainerModelIsIcon(media)) {
            setIconAssetForScene(focusedSceneId, media);
        } else if (immutableAssetContainerModelIsBackground(media)) {
            const background = media.get(AssetContainerTypes.BACKGROUND);
            const bgImage = background?.get(SlideBackgroundModelTypeEnum.IMAGE);

            if (bgImage && bgImage.get('source') === SourceEnum.SOURCE_FREEPIK) {
                if (isAnimatedSticker(bgImage.get('thumbnail_url'))) {
                    setIconSouceDataForScene(
                        focusedSceneId,
                        buildFreepikIconSourceData(
                            bgImage.get('thumbnail_url'),
                            bgImage.get('source_id'),
                        ),
                    );
                } else {
                    await applyFreepikStaticSticker(
                        focusedSceneId,
                        background,
                        bgImage.get('source_id'),
                        { method: SetMediaAssetForSceneOperationMethod.CLICK },
                    );
                }
            } else {
                // Clicking a recording applies it to the focused scene; when that
                // scene is a talking head this runs the legacy recommendation
                // pipeline, so chat users' recordings are handed to the chat
                // instead, which rebuilds the supporting scenes itself.
                const handoff = await handOffRecordingAppliedToTalkingHeadScene({
                    sceneId: focusedSceneId,
                    mediaSourceData: background,
                    isSticker,
                });
                if (handoff !== 'notApplicable') {
                    if (handoff === 'failed') {
                        Toast.error('Sorry, something went wrong with your recording.');
                    }
                    return;
                }
                setMediaAssetForScene({
                    sceneId: focusedSceneId,
                    mediaSourceData: background,
                    targetAssetId: null,
                    method: SetMediaAssetForSceneOperationMethod.CLICK,
                    isRecommendedMedia: isMediaQuerySuggestionResult,
                    isSticker,
                });
            }
        }
        if (!isArticleMedia && !isGenerativeMediaTabContext) {
            dispatch(logApplyFromBackgroundTab(media, result_index));
        }
    }, [
        media,
        focusedSceneId,
        setIconAssetForScene,
        setIconSouceDataForScene,
        setMediaAssetForScene,
        applyFreepikStaticSticker,
        handOffRecordingAppliedToTalkingHeadScene,
        isMediaQuerySuggestionResult,
        isArticleMedia,
        isGenerativeMediaTabContext,
        dispatch,
        result_index,
        isSticker,
    ]);

    const applyAsset = React.useCallback(() => {
        if (selectedSceneAssetId && isEditingContainerVisible()) {
            applyAssetToSelectedAssetContainer();
        } else {
            applyAssetToFocusedScene();
        }
    }, [selectedSceneAssetId, applyAssetToSelectedAssetContainer, applyAssetToFocusedScene]);

    const handleSimilarSearch = React.useCallback(() => {
        if (!media) throw new Error('Media is not defined');
        dispatch(setIsSimilarSearchContext(true));
        dispatch(setSimilarSearchOriginalAsset({ originalAsset: media }));
        dispatch(triggerSimilarAssetSearch(media));
        analytics.track('Similar media search clicked');
    }, [media, dispatch]);

    let badge = null;
    let colClass = 'col-md-6';

    let asset_node = null;
    let asset_name: string | null = null;
    let title_node = null;
    let menu_btn = null;
    let source: number = -1;
    let sourceId: string | null = null;
    let assetType: AssetTypes | null = null;
    let assetContainerType: AssetContainerTypes | null = null;
    let hasVoiceover: boolean = false;

    if (media && immutableAssetContainerModelIsBackground(media)) {
        const background = media.get(AssetContainerTypes.BACKGROUND);
        assetContainerType = AssetContainerTypes.BACKGROUND;
        if (immutableSlideBackgroundModelIsImmutableImageBackgroundModel(background)) {
            const _media = background.get(SlideBackgroundModelTypeEnum.IMAGE);
            source = _media.get('source');
            sourceId = _media.get('source_id');
            assetType = AssetTypes.IMAGE;
            badge = PremiumMediaChecker.badge_required_for_source(
                _media.get('source'),
                AssetContainerTypes.BACKGROUND,
            );
            asset_node = (
                <ImageAsset
                    thumbnailUrl={_media.get('thumbnail_url')}
                    width={_media.get('width')}
                    height={_media.get('height')}
                    source={_media.get('source')}
                    isEditorial={_media.get('is_editorial')}
                    onClick={applyAsset}
                    isSticker={isSticker}
                />
            );
            asset_name = _media.get('title');
        } else if (immutableSlideBackgroundModelIsImmutableVideoBackgroundModel(background)) {
            const _media = background.get(SlideBackgroundModelTypeEnum.VIDEO);
            source = _media.get('source');
            sourceId = _media.get('source_id');
            hasVoiceover = !!_media.get('has_voiceover');
            assetType = AssetTypes.VIDEO;
            badge = PremiumMediaChecker.badge_required_for_source(
                _media.get('source'),
                AssetContainerTypes.BACKGROUND,
            );
            let duration = null;

            const isSearchContextWithValidDuration =
                searchContext &&
                !SEARCH_CONTEXT_MISSING_VIDEO_DURATION.some((missingDurationCtx) =>
                    searchContext.includes(missingDurationCtx),
                );
            if (
                isSearchContextWithValidDuration ||
                _media.get('source') === SourceEnum.SOURCE_AI_GENERATED
            ) {
                duration = _media.get('duration');
            }
            asset_node = (
                <VideoAsset
                    width={_media.get('width')}
                    height={_media.get('height')}
                    source={_media.get('source')}
                    posterUrl={_media.get('poster_url')}
                    thumbnailUrl={_media.get('thumbnail_url')}
                    duration={duration}
                    isEditorial={_media.get('is_editorial')}
                    onClick={applyAsset}
                    isSticker={isSticker}
                />
            );
            asset_name = _media.get('title');
        }
    } else if (media && immutableAssetContainerModelIsIcon(media)) {
        const asset = media.get(AssetContainerTypes.ICON);
        assetContainerType = AssetContainerTypes.ICON;
        badge = PremiumMediaChecker.badge_required_for_source(
            asset.get('source'),
            AssetContainerTypes.ICON,
        );
        source = asset.get('source');
        sourceId = asset.get('source_id');
        assetType = AssetTypes.ICON;
        colClass = 'col-md-4';

        if (searchContext === SEARCH_CONTEXT.WAVEFORMS) {
            asset_node = (
                <WaveformAsset
                    source={asset.get('source')}
                    url={asset.get('url')}
                    numColors={asset.get('num_colors')}
                    onClick={applyAsset}
                />
            );
        } else {
            asset_node = (
                <IconAsset
                    source={asset.get('source')}
                    url={asset.get('url')}
                    numColors={asset.get('num_colors')}
                    onClick={applyAsset}
                />
            );
        }
    }

    let title_string = '';
    if (searchContext === SEARCH_CONTEXT.UPLOADED_ELEMENTS) {
        title_string = assetContainerType === AssetContainerTypes.BACKGROUND ? 'Sticker' : 'Icon';
    } else if (searchContext === SEARCH_CONTEXT.UPLOADED_VISUALS) {
        title_string = asset_name ?? '';
    }
    if (title_string) {
        title_node = (
            <div
                className={`position-absolute top-0 start-0 m-l5-4 font-l5 font-l5--subtitle-sm text-truncate ${styles['details-indicator']} ${styles['asset-name']} hide-while-dragging hidden-soft`}
                title={title_string}
            >
                {title_string}
            </div>
        );
    }

    let similar_search_node = null;
    const excludedSources = [
        SourceEnum.SOURCE_TENOR,
        SourceEnum.SOURCE_ARTICLE,
        SourceEnum.SOURCE_DAM,
        SourceEnum.SOURCE_USER_STICKERS,
    ];
    if (
        !isArticleMedia &&
        showAIMediaDropdown &&
        media &&
        (assetType === AssetTypes.IMAGE || assetType === AssetTypes.VIDEO) &&
        !excludedSources.includes(source)
    ) {
        similar_search_node = (
            <BackgroundAssetAIMediaDropdown
                media={media}
                handleSimilarSearch={handleSimilarSearch}
                source={source}
            />
        );
    }

    const getFavoritesOption = React.useCallback(
        (isPremiumMedia: boolean) => {
            if (!media) throw new Error('Media is not defined');

            const cannotFavouriteContext = [
                SEARCH_CONTEXT.UPLOADED_VISUALS,
                ...EDITOR_ELEMENTS_TAB_CONTEXTS,
                ...EDITOR_DAM_TAB_CONTEXTS,
            ];
            if (
                cannotFavouriteContext.includes(
                    searchContext as (typeof cannotFavouriteContext)[number],
                )
            ) {
                return null;
            }

            const isPremiumMediaAndFreeUser =
                SERVER.ANALYTICS.plan_name === FREE_PLAN_NAME && isPremiumMedia;

            const getFavoriteMediaOption = () => {
                let text = 'Add to favorites';
                let iconClassName = 'material-symbols-outlined';
                if (isFavoriteMedia) {
                    text = 'Remove from favorites';
                    iconClassName += ' material-symbols-f1 color-warning-500';
                }
                return {
                    id: 'favorites',
                    text,
                    onSelectData: {
                        type: OptionsDropdownActions.FAVORITES,
                    },
                    icon: 'star',
                    iconClassName,
                };
            };

            const getSavedAIMediaOption = () => {
                let text = 'Add to favorites';
                let icon = 'star';
                let iconClassName = 'material-symbols-outlined';
                let disabled = false;
                if (isSavingAIMedia) {
                    text = 'Adding to favorites...';
                    icon = 'spinner-icon';
                    iconClassName += ' color-primary';
                    disabled = true;
                } else if (isFavoriteMedia) {
                    text = 'Remove from favorites';
                    iconClassName += ' material-symbols-f1 color-warning-500';
                }

                return {
                    id: 'favorites',
                    text,
                    onSelectData: {
                        type: OptionsDropdownActions.FAVORITES,
                    },
                    icon,
                    iconClassName,
                    disabled,
                };
            };

            // if it's not in the star tab (ie. Tracks tab) - we only show favorited items for the user, so always show the star
            let isOwnedByUser = true;
            if (searchContext === SEARCH_CONTEXT.FAVORITE_VISUALS) {
                // if we're in the favorite tab, we hide stars for those assets that were favorited by someone else
                if (immutableAssetContainerModelIsBackground(media)) {
                    isOwnedByUser = !!media
                        .get(AssetContainerTypes.BACKGROUND)
                        .get('history_index_user_ids')
                        ?.some((user_id: number) => SERVER.USER.id === user_id);
                }
            }
            if (!isPremiumMediaAndFreeUser && !isArticleMedia && isOwnedByUser) {
                if (isGenerativeMediaTabContext) {
                    return getSavedAIMediaOption();
                } else if (source !== SourceEnum.SOURCE_AI_GENERATED) {
                    // No save/favorite option for AI media that are not in the generative media tab
                    return getFavoriteMediaOption();
                }
            }
            return null;
        },
        [
            isArticleMedia,
            isFavoriteMedia,
            isGenerativeMediaTabContext,
            isSavingAIMedia,
            media,
            searchContext,
            source,
        ],
    );

    const getDropdownMenuItems = React.useCallback(
        (
            isPremiumMedia: boolean,
        ): Immutable.List<
            DropdownItemProp<{
                type: OptionsDropdownActions;
                folderId?: number;
            }>
        > => {
            if (!media) throw new Error('Media is not defined');
            const dropdownItems: Array<unknown> = [];
            if (destinationFolderList && destinationFolderList.length > 0) {
                dropdownItems.push({
                    id: 'move-to-folder',
                    nestedDropdown: {
                        toggleProps: {
                            text: 'Move to folder',
                            icon: 'folder',
                        },
                        itemPropsList: destinationFolderList.map((folder) => {
                            return {
                                id: `${folder.id}`, // type is string
                                text: folder.name,
                                onSelectData: {
                                    type: OptionsDropdownActions.MOVE_TO_FOLDER,
                                    folderId: folder.id,
                                },
                            };
                        }),
                        limitMenuHeight: true,
                    },
                });
            }

            const favoritesOption = getFavoritesOption(isPremiumMedia);
            if (favoritesOption) {
                dropdownItems.push(favoritesOption);
            }

            if (
                searchContext === SEARCH_CONTEXT.AI_GENERATED_MEDIA ||
                searchContext === SEARCH_CONTEXT.FAVORITE_AI_MEDIA ||
                isUploadSearchContextType(searchContext) ||
                (searchContext === null && isGenerativeMediaTabContext)
            ) {
                dropdownItems.push({
                    id: 'delete',
                    text: 'Delete',
                    onSelectData: { type: OptionsDropdownActions.DELETE },
                    icon: 'delete',
                });
            }

            return ImmutableFromJS([
                {
                    id: 'media-info',
                    text: 'Media info',
                    onSelectData: { type: OptionsDropdownActions.MEDIA_INFO },
                    icon: 'info',
                },
                ...dropdownItems,
            ]) as Immutable.List<
                DropdownItemProp<{
                    type: OptionsDropdownActions;
                    folderId?: number;
                }>
            >;
        },
        [
            media,
            destinationFolderList,
            getFavoritesOption,
            searchContext,
            isGenerativeMediaTabContext,
        ],
    );

    const dropdownItems = React.useMemo(
        () => (media ? getDropdownMenuItems(badge !== PREMIUM_LEVEL.FREE) : null),
        [badge, getDropdownMenuItems, media],
    );

    if (!showDelConfirm && !isArticleMedia && dropdownItems) {
        menu_btn = (
            <div
                className={classNames(
                    'position-absolute bottom-0 end-0 m-l5-4 hide-while-dragging hidden-soft',
                    { 'menu-is-open': uploadMenuIsOpen },
                )}
            >
                <Dropdown
                    myRef={optionsDropdownRef}
                    toggleProps={OptionsDropdownToggleProps}
                    ToggleComponent={IconButton}
                    itemPropsList={dropdownItems}
                    onSelect={onOptionsDropdownSelect}
                    myMenuPath={searchContext ?? 'undefined'} // Search context can be null in MediaQuerySuggestions, in which case we can use a default string for the menu path
                    afterOpen={menuAfterOpen}
                    afterClose={menuAfterClose}
                    closeOnSelect={false}
                    breakOverflow
                />
            </div>
        );
    }

    let delete_confirmation = null;
    if (showDelConfirm) {
        delete_confirmation = (
            <div
                className={`position-absolute top-0 start-0 bottom-0 end-0 d-flex flex-column align-items-center justify-content-center ${styles['asset-overlay']}`}
            >
                <p className="delete-overlay-text my-l5-12">Delete this file?</p>
                <div className="btn-row">
                    <SecondaryButton
                        className="text-white"
                        variant={BUTTON_VARIANTS.LINK}
                        color={BUTTON_COLORS.SIMPLE}
                        size={BUTTON_SIZES.SM}
                        onClick={hideDeleteConfirmation}
                        text="Cancel"
                    />
                    <Button
                        onClick={handleDeleteAsset}
                        text="Delete"
                        color={BUTTON_COLORS.DANGER}
                        size={BUTTON_SIZES.SM}
                    />
                </div>
            </div>
        );
    }

    if (isArticleMedia || fullWidth) {
        colClass = '';
    }

    // Make stickers display in 3 columns
    if (
        searchContext === SEARCH_CONTEXT.FREEPIK_STICKERS ||
        searchContext === SEARCH_CONTEXT.UPLOADED_ELEMENTS
    ) {
        colClass = 'col-md-4';
    }

    // Make waveforms display in 2 columns (larger than icons/stickers)
    if (searchContext === SEARCH_CONTEXT.WAVEFORMS) {
        colClass = 'col-md-6';
    }

    if (!media) {
        return null;
    }

    if (assetType === null || source === null || sourceId === null || assetContainerType === null) {
        throw new Error('Asset type, source, or source ID is not defined');
    }

    const dragulaAssetType: DragAssetTypes = (() => {
        if (isSticker) {
            if (searchContext === SEARCH_CONTEXT.WAVEFORMS) {
                return MiscDragAssetTypes.WAVEFORM;
            }
            return MiscDragAssetTypes.STICKER;
        }
        return assetContainerType;
    })();

    const mediaUuid = getAssetUuidFromAssetContainer(media);

    return (
        <div
            ref={backgroundAssetCallbackRef}
            className={classNames('asset-holder asset-draggable', colClass)}
            onContextMenu={blockContextMenu}
            data-is-media-query-result={isMediaQuerySuggestionResult}
            data-is-article-media={isArticleMedia}
            data-dragula-media-source={source}
            // NOTE: these are queried via `el.dataset.dragulaAssetType` and `el.dataset.dragulaMediaAssetType`
            data-dragula-media-asset-type={assetType}
            data-dragula-asset-type={dragulaAssetType}
            data-dragula-has-voiceover={hasVoiceover}
            data-dragula-media-index={result_index}
            data-dragula-media-id={media_id}
            data-dragula-media-url={sourceId}
            data-dragula-media-uuid={mediaUuid}
            data-dragula-search-context={searchContext}
            data-dragula-carousel-category={category}
            data-testid="background-asset"
        >
            <div className="position-relative w-100 h-100">
                {asset_node}
                {title_node}
                {!hideControls && (
                    <>
                        {similar_search_node}
                        {menu_btn}
                        {delete_confirmation}
                    </>
                )}
            </div>
        </div>
    );
});

export default BackgroundAsset;
